import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  PhoneCall, 
  Calendar,
  Globe,
  Search,
  ShieldCheck,
  User,
  LogOut,
  Settings,
  UserCheck,
  CreditCard
} from 'lucide-react';
import BrandLogo from './BrandLogo';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';
import InstantBookingModal from '../booking/InstantBookingModal';
import AuthModal from '../auth/AuthModal';
import ProfileModal from '../auth/ProfileModal';
import SubscriptionModal from '../subscription/SubscriptionModal';
import { formatShortName } from '../../utils/helpers';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);

  const location = useLocation();
  const { currentLang, setLanguage, t, languages } = useLanguage();
  const { user, isAuthenticated, isClient, isAdmin, isLawyer, logout } = useAuth();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setLangDropdownOpen(false);
    setUserDropdownOpen(false);
    setMobileLangOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Close menus on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setLangDropdownOpen(false);
        setUserDropdownOpen(false);
        setMobileLangOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close user dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', anchor: 'hero', label: t('nav_home', 'Bosh sahifa') },
    { to: '/about', anchor: 'about', label: t('nav_about', 'Advokatlar haqida') },
    { to: '/services', anchor: 'services', label: t('nav_services', 'Amaliyotlar') },
    { to: '/lawyers', anchor: 'lawyers', label: t('nav_lawyers', 'Advokatlar') },
    { to: '/check-status', label: t('nav_check_booking', 'Arizani tekshirish') },
    { to: '/contact', anchor: 'offices', label: t('nav_contact', 'Aloqa') },
  ];

  const handleNavClick = (e, link) => {
    if (location.pathname === '/' && link.anchor) {
      e.preventDefault();
      const el = document.getElementById(link.anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
  };

  const currentLangObj = languages.find((l) => l.code === currentLang) || languages[0];

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-200 ${
          isScrolled
            ? 'bg-[#05080e]/95 backdrop-blur-md shadow-2xl shadow-black/80 border-b border-[#c59b27]/30'
            : 'bg-[#05080e] border-b border-[#141d2d]'
        }`}
      >
        <div className="max-w-[1440px] w-full mx-auto px-2 sm:px-3 lg:px-3 xl:px-6">
          <div className="flex items-center justify-between h-16 sm:h-18">
            
            {/* Logo */}
            <Link to="/" className="shrink-0 mr-1.5 xl:mr-4">
              <BrandLogo />
            </Link>

            {/* Desktop Navigation Links (Compact scale ensures 100% fit on all screen resolutions) */}
            <nav className="hidden lg:flex items-center space-x-1 lg:space-x-1.5 xl:space-x-2.5 2xl:space-x-4 shrink-0">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={(e) => handleNavClick(e, link)}
                    className={`text-[9.5px] xl:text-[10.5px] 2xl:text-xs font-semibold uppercase tracking-tight xl:tracking-[0.03em] 2xl:tracking-[0.06em] whitespace-nowrap transition-all relative py-1 px-0.5 xl:px-1.5 ${
                      isActive
                        ? 'text-[#c59b27] font-bold'
                        : 'text-slate-300 hover:text-[#c59b27]'
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#c59b27] rounded-full shadow-sm shadow-[#c59b27]" />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right Action Items */}
            <div className="hidden lg:flex items-center space-x-1 sm:space-x-1.5 xl:space-x-2 shrink-0">
              {/* Notification Bell (SMS & Confirmation Status) */}
              <NotificationBell onAuthClick={() => setAuthModalOpen(true)} />

              {/* Language Switcher */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center space-x-1 text-[11px] text-slate-300 hover:text-white px-2 py-1 rounded border border-slate-700/60 bg-[#0a0f19] transition-colors cursor-pointer"
                >
                  <Globe className="w-3 h-3 text-[#c59b27]" />
                  <span className="uppercase font-semibold text-[10px]">{currentLangObj.code}</span>
                  <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
                </button>

                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-[#0c1322] border border-[#c59b27]/40 rounded-lg shadow-xl py-1 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3.5 py-1.5 text-xs flex items-center justify-between hover:bg-[#15233b] transition-colors ${
                          currentLang === lang.code ? 'text-[#c59b27] font-bold bg-[#111a2d]' : 'text-slate-300'
                        }`}
                      >
                        <span>{lang.name}</span>
                        <span className="text-[10px] uppercase text-slate-500">{lang.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User Authentication Status */}
              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center space-x-1.5 py-1 px-2 rounded-lg border border-[#c59b27]/40 bg-[#0d1524] hover:bg-[#152238] transition-all cursor-pointer"
                    title={user?.fullName}
                  >
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.fullName} className="w-5 h-5 rounded-full object-cover border border-[#c59b27]" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-[#c59b27] text-black font-bold text-[10px] flex items-center justify-center">
                        {user?.fullName?.charAt(0) || 'U'}
                      </div>
                    )}
                    <span className="text-[11px] font-semibold text-slate-200 max-w-[70px] xl:max-w-[90px] truncate">
                      {formatShortName(user?.fullName) || 'Akkaunt'}
                    </span>
                    <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
                  </button>

                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-[#070c16] border border-[#c59b27]/40 rounded-xl shadow-2xl shadow-black/90 py-1.5 z-50">
                      <div className="px-3.5 py-2 border-b border-[#1b2b46]">
                        <p className="text-xs font-bold text-slate-200 truncate">{user?.fullName}</p>
                        {user?.username && (
                          <p className="text-[10px] text-[#c59b27] font-mono">@{user.username}</p>
                        )}
                        <p className="text-[10px] text-slate-400 font-mono truncate">{user?.phone || user?.email}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setUserDropdownOpen(false);
                          setProfileModalOpen(true);
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs text-slate-300 hover:text-white hover:bg-[#111c2e] flex items-center"
                      >
                        <User className="w-3.5 h-3.5 mr-2 text-[#c59b27]" />
                        {t('my_profile', 'Mening Profilim')}
                      </button>

                      {/* Subscription Status & Activation */}
                      <button
                        type="button"
                        onClick={() => {
                          setUserDropdownOpen(false);
                          setSubModalOpen(true);
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs text-amber-300 hover:text-white hover:bg-[#111c2e] flex items-center justify-between"
                      >
                        <span className="flex items-center min-w-0 pr-1">
                          <CreditCard className="w-3.5 h-3.5 mr-2 text-[#c59b27] shrink-0" />
                          <span className="truncate">{t('sub_monthly_menu', 'Oylik Obuna')}</span>
                        </span>
                        <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-[#c59b27] border border-[#c59b27]/30 shrink-0">
                          {user?.role === 'LAWYER' ? '99 000 UZS' : '35 000 UZS'}
                        </span>
                      </button>

                      {(isAdmin || isLawyer) && (
                        <Link
                          to="/admin/dashboard"
                          className="w-full text-left px-3.5 py-2 text-xs text-[#c59b27] hover:bg-[#111c2e] flex items-center font-semibold"
                        >
                          <Settings className="w-3.5 h-3.5 mr-2" />
                          <span>{isLawyer ? t('lawyer_cabinet_menu', 'Advokat Kabineti') : t('admin_panel_title', 'Admin Boshqaruv')}</span>
                        </Link>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setUserDropdownOpen(false);
                          logout();
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs text-rose-400 hover:bg-[#111c2e] flex items-center border-t border-[#1b2b46]/60 mt-1"
                      >
                        <LogOut className="w-3.5 h-3.5 mr-2" />
                        {t('logout', 'Chiqish')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAuthModalOpen(true)}
                  className="px-2.5 py-1 rounded border border-[#c59b27]/40 bg-[#0a0f19] hover:bg-[#131d2e] text-slate-200 text-[11px] font-semibold uppercase tracking-wider transition-colors cursor-pointer"
                >
                  {t('auth_login', 'Kirish')}
                </button>
              )}

              {/* Free Consultation CTA Button */}
              <button
                type="button"
                onClick={() => {
                  if (location.pathname === '/') {
                    const el = document.getElementById('booking') || document.getElementById('booking-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                      return;
                    }
                  }
                  setBookingModalOpen(true);
                }}
                className="px-2.5 py-1.5 xl:px-3.5 xl:py-2 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-[10px] xl:text-[11px] font-bold uppercase tracking-[0.03em] shadow-md shadow-[#b38038]/20 transition-all cursor-pointer whitespace-nowrap shrink-0"
              >
                {t('nav_book_cta', 'Qabulga yozilish')}
              </button>
            </div>

            {/* Mobile Actions (Visible on small screens and all external devices) */}
            <div className="flex items-center space-x-1 sm:space-x-1.5 lg:hidden">
              <NotificationBell onAuthClick={() => setAuthModalOpen(true)} />

              {/* Mobile Language Switcher Dropdown (100% visible on all devices) */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMobileLangOpen(!mobileLangOpen)}
                  className="flex items-center space-x-1 px-1.5 sm:px-2 py-1.5 rounded-lg border border-[#c59b27]/50 bg-[#0d1624] text-slate-200 active:scale-95 transition-all cursor-pointer shadow-sm"
                  title="Tilni tanlash / Выбор языка / Language"
                  aria-label="Tilni tanlash"
                >
                  <Globe className="w-3.5 h-3.5 text-[#c59b27]" />
                  <span className="uppercase font-bold text-[11px] text-[#c59b27]">
                    {currentLang}
                  </span>
                  <ChevronDown className="w-2.5 h-2.5 text-slate-400" />
                </button>

                {mobileLangOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-[#0c1322] border border-[#c59b27]/60 rounded-xl shadow-2xl shadow-black/95 py-1.5 z-50 animate-in fade-in zoom-in-95">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setLanguage(lang.code);
                          setMobileLangOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-[#15233c] transition-colors ${
                          currentLang === lang.code ? 'text-[#c59b27] font-bold bg-[#111a2d]' : 'text-slate-300'
                        }`}
                      >
                        <span className="flex items-center space-x-1.5">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                        <span className="text-[10px] uppercase font-mono text-slate-400">{lang.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Book CTA on Mobile (Shows on sm: screens to prevent header squashing on tiny phones) */}
              <button
                type="button"
                onClick={() => setBookingModalOpen(true)}
                className="hidden sm:inline-flex px-2.5 py-1.5 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] text-white text-[11px] font-bold uppercase tracking-wider shadow-sm cursor-pointer whitespace-nowrap"
              >
                {t('nav_book_short', 'Qabul')}
              </button>

              {/* Hamburger Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-1.5 sm:p-2 rounded-lg bg-[#0d1524] border border-slate-700 text-slate-200 cursor-pointer"
                aria-label="Menyu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

          </div>
        </div>

        {/* Mobile Navigation Drawer with Overlay Backdrop */}
        {mobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 top-16 bg-black/70 backdrop-blur-xs z-30 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="relative z-40 lg:hidden bg-[#070c16] border-b border-[#c59b27]/30 px-4 pt-3 pb-6 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto shadow-2xl animate-in slide-in-from-top-2">
              
              {/* Quick Language Pill Bar at TOP of Drawer */}
              <div className="flex items-center justify-between pb-2.5 border-b border-[#141d2d]">
                <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center">
                  <Globe className="w-3.5 h-3.5 text-[#c59b27] mr-1.5" />
                  {t('select_language', 'Tilni tanlang')}:
                </span>
                <div className="flex space-x-1.5">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => {
                        setLanguage(l.code);
                        setMobileMenuOpen(false);
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase transition-all cursor-pointer ${
                        currentLang === l.code
                          ? 'bg-[#c59b27] text-black shadow-sm font-black'
                          : 'bg-[#121c2e] text-slate-300 hover:text-white border border-slate-700/60'
                      }`}
                    >
                      {l.code}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Mobile Appointment CTA Button */}
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setBookingModalOpen(true);
                }}
                className="w-full py-2.5 px-4 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] text-white text-xs font-bold uppercase tracking-wider shadow-md text-center flex items-center justify-center space-x-2 cursor-pointer"
              >
                <Calendar className="w-4 h-4" />
                <span>{t('nav_book_cta', 'Qabulga yozilish')}</span>
              </button>

              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => {
                    setMobileMenuOpen(false);
                    if (location.pathname === '/' && link.anchor) {
                      e.preventDefault();
                      const el = document.getElementById(link.anchor);
                      if (el) {
                        setTimeout(() => {
                          el.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }
                    }
                  }}
                  className="block py-2 text-xs uppercase tracking-wider font-semibold text-slate-200 hover:text-[#c59b27]"
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-3 border-t border-slate-800 space-y-2">
                {isAuthenticated ? (
                  <div className="space-y-2 py-1">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setProfileModalOpen(true);
                        }}
                        className="text-xs text-[#c59b27] font-semibold truncate max-w-[200px]"
                      >
                        {t('my_profile', 'Profil')}: {user?.fullName}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          logout();
                        }}
                        className="text-xs text-rose-400 hover:text-rose-300"
                      >
                        {t('logout', 'Chiqish')}
                      </button>
                    </div>

                    {(isAdmin || isLawyer) && (
                      <Link
                        to="/admin/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="w-full py-2 px-3 rounded bg-[#0e172a] border border-[#c59b27]/40 text-xs text-[#c59b27] font-semibold flex items-center justify-between"
                      >
                        <span className="flex items-center">
                          <Settings className="w-3.5 h-3.5 mr-2" />
                          <span>{isLawyer ? 'Advokat Shaxsiy Kabineti' : t('admin_panel_title', 'Admin Boshqaruv')}</span>
                        </span>
                        <span>→</span>
                      </Link>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setSubModalOpen(true);
                      }}
                      className="w-full py-2 px-3 rounded bg-[#0b1220] border border-amber-500/30 text-xs text-amber-300 font-semibold flex items-center justify-between cursor-pointer"
                    >
                      <span className="flex items-center">
                        <CreditCard className="w-3.5 h-3.5 mr-2 text-[#c59b27]" />
                        <span>Oylik Obuna</span>
                      </span>
                      <span className="text-[10px] text-[#c59b27] font-bold">
                        {user?.role === 'LAWYER' ? '99 000 UZS' : '35 000 UZS'}
                      </span>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setAuthModalOpen(true);
                    }}
                    className="w-full py-2 rounded bg-[#111c2e] border border-[#c59b27]/40 text-xs text-[#c59b27] font-bold uppercase cursor-pointer"
                  >
                    {t('auth_login', 'Tizimga kirish')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </header>

      {/* Modals */}
      <InstantBookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => setBookingModalOpen(false)} 
      />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />

      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />

      <SubscriptionModal
        isOpen={subModalOpen}
        onClose={() => setSubModalOpen(false)}
        initialType={user?.role === 'LAWYER' ? 'LAWYER' : 'CLIENT'}
      />
    </>
  );
}
